/**
 * Response Builder Jobs Repository
 * 
 * Handles rb_jobs collection operations (audit log).
 * Phase C1 - Data Model Activation
 */

import {
  collection,
  doc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  type Firestore,
} from 'firebase/firestore';

import type { RbJob, CreateJobInput } from './types/rb';

/**
 * Create a job record (audit log entry)
 */
export async function createJob(
  db: Firestore,
  jobId: string,
  input: CreateJobInput
): Promise<RbJob> {
  const now = Timestamp.now();
  const jobRef = doc(db, 'rb_jobs', jobId);

  const job: Omit<RbJob, 'id'> = {
    workspaceId: input.workspaceId,
    type: input.type,
    action: input.action,
    documentId: input.documentId,
    status: input.status,
    message: input.message,
    meta: input.meta,
    createdAt: now,
    createdByUserId: input.createdByUserId,
  };

  await setDoc(jobRef, job);

  return {
    id: jobId,
    ...job,
  };
}

/**
 * List jobs for a document
 * Ordered by createdAt descending (newest first)
 * 
 * Used in Library Detail UI (C3) to show ingestion history
 */
export async function listJobsForDocument(
  db: Firestore,
  documentId: string,
  maxResults: number = 50
): Promise<RbJob[]> {
  const jobsRef = collection(db, 'rb_jobs');
  const q = query(
    jobsRef,
    where('documentId', '==', documentId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as RbJob[];
}

/**
 * Get last ingestion job timestamp for a workspace
 * 
 * Used in Status Page (C8)
 */
export async function getLastIngestionTimestamp(
  db: Firestore,
  workspaceId: string
): Promise<Timestamp | null> {
  const jobsRef = collection(db, 'rb_jobs');
  const q = query(
    jobsRef,
    where('workspaceId', '==', workspaceId),
    where('type', '==', 'ingestion'),
    orderBy('createdAt', 'desc'),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const job = snapshot.docs[0].data() as RbJob;
  return job.createdAt;
}
